import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { ArrowRight, Sparkles, Lock, BookOpen, Clock, Heart, Compass } from "lucide-react";

export default function LOSLanding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tmpl, setTmpl] = useState(null);
  const [myResp, setMyResp] = useState(null);

  useEffect(() => {
    api.get("/los/template").then((r) => setTmpl(r.data));
    if (user) api.get("/los/response").then((r) => setMyResp(r.data)).catch(() => {});
  }, [user]);

  const start = () => {
    if (!user) { navigate("/auth/login?next=/los/scan"); return; }
    navigate("/los/scan");
  };

  return (
    <div className="min-h-screen bg-[#081328] text-[#F7F6F2]" data-testid="los-landing">
      {/* ─── HERO ──────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-30 pointer-events-none"
             style={{ background: "radial-gradient(ellipse at 30% 30%, #4B2E83 0%, transparent 50%), radial-gradient(ellipse at 70% 70%, #D4AF37 0%, transparent 60%)" }} />
        <div className="relative container-sat pt-32 pb-20 lg:pb-28">
          <div className="grid lg:grid-cols-[1.15fr_1fr] gap-12 lg:gap-20 items-center">
            {/* Left: copy + CTAs */}
            <div className="max-w-2xl">
              <div className="text-[#D4AF37] text-xs tracking-[0.4em] uppercase mb-6">SAT · Life Operating System</div>
              <h1 className="serif text-5xl md:text-7xl lg:text-[5.6rem] xl:text-[6.4rem] font-light tracking-tight leading-[1.04]">
                Der tiefe<br />
                <span className="italic" style={{ color: "#D4AF37" }}>Scan deines Lebens.</span>
              </h1>
              <p className="text-white/70 text-lg md:text-xl mt-8 max-w-xl leading-relaxed">
                Zwölf Räume. Über zweihundert Fragen. Keine Tests, keine Auswertung im Sinne von Bewertung.<br/>
                Nur ein still gehaltener Spiegel, der dich an dich selbst erinnert.
              </p>
              <div className="mt-12 flex flex-wrap items-center gap-4">
                <button onClick={start}
                  className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] text-[#081328] font-semibold rounded-full text-base hover:bg-[#e3c252] transition tracking-wide"
                  data-testid="los-start-btn">
                  {myResp?.progress > 0 ? "Reise fortsetzen" : "Den Scan beginnen"} <ArrowRight size={16} />
                </button>
                {myResp?.progress > 0 && (
                  <span className="text-white/60 text-sm">
                    Du hast bereits <b className="text-[#D4AF37]">{myResp.progress}%</b> abgeschlossen.
                  </span>
                )}
              </div>
            </div>

            {/* Right: 12-Räume Kompass-Visual */}
            <div className="hidden lg:block relative">
              <RaumCompass />
            </div>
          </div>
        </div>
      </section>

      {/* ─── FEATURES ─────────────────────────────────────────── */}
      <section className="container-sat py-20 lg:py-24 border-t border-white/5">
        <div className="grid md:grid-cols-3 gap-8 lg:gap-12 max-w-6xl mx-auto">
          <Feature icon={<BookOpen size={24} />} title="204 Fragen" body="Sortiert nach 12 Lebensräumen — von der Kindheit bis zum Vermächtnis." />
          <Feature icon={<Clock size={24} />} title="In deinem Tempo" body="Jede Antwort wird automatisch gespeichert. Komm immer wieder zurück." />
          <Feature icon={<Heart size={24} />} title="Auf Augenhöhe" body="Keine Bewertung. Nur das, was du dich endlich selbst fragen darfst." />
        </div>
      </section>

      {/* ─── 12 ROOMS PREVIEW — wider grid on desktop ──────────── */}
      {tmpl && (
        <section className="container-sat py-20 lg:py-28 border-t border-white/5">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-12 lg:mb-16">
            <div>
              <div className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-3">Die 12 Räume</div>
              <h2 className="serif text-3xl md:text-5xl lg:text-6xl font-light">Wo der Scan dich hinführt.</h2>
            </div>
            <p className="text-white/45 italic text-sm max-w-sm">Jeder Raum öffnet eine Tür — manchmal die, vor der du jahrelang stehen geblieben bist.</p>
          </div>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8">
            {tmpl.sections.map((s, idx) => (
              <div key={s.key}
                   className="card-lift relative rounded-2xl border border-white/8 bg-white/[0.02] p-6 lg:p-7 overflow-hidden"
                   style={{ borderLeft: `3px solid ${s.color}` }}
                   data-testid={`section-preview-${s.key}`}>
                <div className="text-[10px] uppercase tracking-[0.3em] mb-1.5" style={{ color: s.color }}>
                  Raum {String(idx + 1).padStart(2, "0")}
                </div>
                <div className="serif text-2xl mt-1 leading-tight">{s.title}</div>
                <div className="text-white/55 text-sm mt-2 italic leading-relaxed">{s.intro}</div>
                <div className="text-white/40 text-xs mt-4 flex items-center gap-1.5">
                  <Compass size={11} /> {s.questions.length} Fragen
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ─── CTA ──────────────────────────────────────────────── */}
      <section className="container-sat py-24 lg:py-32 text-center border-t border-white/5">
        <div className="max-w-3xl mx-auto">
          <Sparkles size={28} className="mx-auto text-[#D4AF37] mb-6" />
          <h3 className="serif text-3xl md:text-5xl lg:text-6xl font-light leading-tight">
            Du brauchst keine Antworten — du brauchst die richtigen Fragen.
          </h3>
          <p className="text-white/55 mt-6 lg:text-lg">Beginne den Scan jetzt. Alles bleibt gesichert in deinem Konto.</p>
          <button onClick={start}
            className="mt-10 inline-flex items-center gap-2 px-9 py-4 bg-[#D4AF37] text-[#081328] font-semibold rounded-full hover:bg-[#e3c252] transition"
            data-testid="los-cta-btn">
            {user ? "Den Scan öffnen" : (<><Lock size={14} /> Anmelden &amp; starten</>)}
            <ArrowRight size={14} />
          </button>
        </div>
      </section>
    </div>
  );
}

const Feature = ({ icon, title, body }) => (
  <div>
    <div className="text-[#D4AF37] mb-3">{icon}</div>
    <h4 className="serif text-xl mb-2">{title}</h4>
    <p className="text-white/55 text-sm lg:text-base leading-relaxed">{body}</p>
  </div>
);

// ── 12-Räume Kompass — radiales SVG mit 12 gleichmäßig verteilten Türen ──
const RaumCompass = () => {
  const labels = ["Wurzeln", "Identität", "Körper", "Beziehungen", "Berufung", "Geld",
                  "Sexualität", "Spiritualität", "Schatten", "Vermächtnis", "Tod", "Licht"];
  const R = 200;
  return (
    <div className="relative w-full max-w-[520px] mx-auto aspect-square" data-testid="los-raum-compass">
      <svg viewBox="-360 -280 720 560" className="w-full h-full">
        <defs>
          <radialGradient id="los-core" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.25" />
            <stop offset="55%" stopColor="#4B2E83" stopOpacity="0.12" />
            <stop offset="100%" stopColor="#081328" stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle r={R + 12} fill="url(#los-core)" />
        {/* outer ring */}
        <circle r={R} fill="none" stroke="rgba(212,175,55,0.25)" strokeWidth="0.8" />
        <circle r={R - 50} fill="none" stroke="rgba(164,140,255,0.18)" strokeWidth="0.6" strokeDasharray="2 4" />
        <circle r={R - 100} fill="none" stroke="rgba(212,175,55,0.12)" strokeWidth="0.5" />
        {/* 12 doors */}
        {labels.map((label, i) => {
          const a = (i / 12) * Math.PI * 2 - Math.PI / 2;
          const x = Math.cos(a) * R;
          const y = Math.sin(a) * R;
          const lx = Math.cos(a) * (R + 32);
          const ly = Math.sin(a) * (R + 32);
          return (
            <g key={i}>
              <line x1="0" y1="0" x2={x} y2={y} stroke="rgba(247,246,242,0.08)" strokeWidth="0.5" />
              <circle cx={x} cy={y} r="5" fill="#D4AF37" />
              <circle cx={x} cy={y} r="9" fill="#D4AF37" opacity="0.18" />
              <text x={lx} y={ly}
                    fontSize="11"
                    fill="rgba(247,246,242,0.7)"
                    fontFamily="Montserrat"
                    textAnchor={Math.abs(lx) < 18 ? "middle" : lx > 0 ? "start" : "end"}
                    dominantBaseline="middle"
                    letterSpacing="1.6">
                {label.toUpperCase()}
              </text>
            </g>
          );
        })}
        {/* center label */}
        <text x="0" y="-5" textAnchor="middle" fontSize="11" letterSpacing="4"
              fill="#D4AF37" fontFamily="Montserrat" fontWeight="600">LIFE</text>
        <text x="0" y="14" textAnchor="middle" fontSize="11" letterSpacing="4"
              fill="#D4AF37" fontFamily="Montserrat" fontWeight="600">MAP</text>
      </svg>
      {/* slow rotation halo */}
      <div className="absolute inset-0 rounded-full pointer-events-none"
           style={{
             background: "conic-gradient(from 0deg, transparent 0deg, rgba(212,175,55,0.08) 90deg, transparent 180deg, rgba(75,46,131,0.08) 270deg, transparent 360deg)",
             animation: "compass-rotate 38s linear infinite",
             mixBlendMode: "screen",
           }} />
      <style>{`@keyframes compass-rotate { from { transform: rotate(0deg);} to { transform: rotate(360deg);} }`}</style>
    </div>
  );
};
